import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  Button,
  TextField,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { useTranslation } from "react-i18next";
import { collection, doc, getDoc, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../firebaseConfig";
import StudentLayout from "../components/student/StudentLayout";
import StudentBreadcrumb from "../components/student/StudentBreadcrumb";
import StudentExerciseAI from "../components/StudentExerciseAI";
import { PhotoProvider, PhotoView } from "react-photo-view";
import "react-photo-view/dist/react-photo-view.css";
import { AiStatusChip, AssistanceTypeChip, VerificationLine } from "../components/student/ExerciseStatusChips";
import { EXERCISE_MODE_LABELS } from "../theme/tokens";

const isImageFile = (fileName) => /\.(jpe?g|png|gif|webp|heic|heif|bmp)$/i.test(fileName || "");

// Backward-compatible: exercises created before multi-image support
// only have a scalar subjectPdfUrl/subjectPdfName — treat that as a
// one-element gallery so both cases render through the same path.
const getSubjectImages = (exercise) => {
  if (exercise?.subjectImages?.length) return exercise.subjectImages;
  if (isImageFile(exercise?.subjectPdfName)) {
    return [{ url: exercise.subjectPdfUrl, name: exercise.subjectPdfName }];
  }
  return [];
};

const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (typeof value.toMillis === "function") return new Date(value.toMillis());
  if (typeof value.seconds === "number") return new Date(value.seconds * 1000);
  if (value instanceof Date) return value;
  if (typeof value === "number" || typeof value === "string") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
};

const STATUS_FILTERS = [
  { value: "all", label: "All Statuses" },
  { value: "active", label: "مفعّل" },
  { value: "inactive", label: "معطّل" },
];

const MODE_FILTERS = [
  { value: "all", label: "All Assistance Types" },
  { value: "hints", label: EXERCISE_MODE_LABELS.hints },
  { value: "guided", label: EXERCISE_MODE_LABELS.guided },
  { value: "direct", label: EXERCISE_MODE_LABELS.direct },
];

const filterTextColor = "#7d8797";

const filterControlSx = {
  bgcolor: "#fff",
  borderRadius: "12px",
  "& .MuiOutlinedInput-root": {
    height: 42,
    borderRadius: "12px",
    color: filterTextColor,
    fontSize: 14,
    fontWeight: 500,
    backgroundColor: "#fff",
    boxShadow: "0 1px 3px rgba(15, 35, 85, 0.04)",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#dde7f5",
    borderWidth: "1.5px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#cddbef",
  },
  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#b7c9e5",
    borderWidth: "1.5px",
  },
  "& .MuiInputBase-input": {
    px: 1.25,
  },
  "& input::placeholder": {
    color: filterTextColor,
    opacity: 1,
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    color: filterTextColor,
    pr: 4.5,
  },
  "& .MuiSelect-icon": {
    color: "#0b2a63",
    fontSize: 22,
    right: 16,
  },
};

export default function StudentExercises() {
  const { t, i18n } = useTranslation();
  const { groupId } = useParams();
  const [exercises, setExercises] = useState([]);
  const [selectedExerciseId, setSelectedExerciseId] = useState(null);
  const [groupName, setGroupName] = useState("");
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [modeFilter, setModeFilter] = useState("all");

  useEffect(() => {
    if (!groupId) return undefined;

    const exercisesRef = query(
      collection(db, "groups", groupId, "exercises"),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(exercisesRef, (snapshot) => {
      setExercises(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    return unsubscribe;
  }, [groupId]);

  useEffect(() => {
    if (!groupId) return;
    getDoc(doc(db, "groups", groupId)).then((snap) => {
      if (snap.exists()) {
        const groupData = snap.data() || {};
        setGroupName(groupData.groupName || groupData.name || "");
      }
    });
  }, [groupId]);

  const selectedExercise = exercises.find((exercise) => exercise.id === selectedExerciseId) || null;
  const locale = i18n.resolvedLanguage || i18n.language || "en";

  const formatCreatedAt = (createdAt) => {
    const date = toDate(createdAt);
    if (!date) return t("Creation date unavailable");

    return new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(date);
  };

  const filteredExercises = exercises.filter((exercise) => {
    if (searchText.trim() && !exercise.title?.toLowerCase().includes(searchText.trim().toLowerCase())) {
      return false;
    }
    if (statusFilter === "active" && !exercise.aiEnabled) return false;
    if (statusFilter === "inactive" && exercise.aiEnabled) return false;
    if (modeFilter !== "all" && exercise.mode !== modeFilter) return false;
    return true;
  });

  return (
    <StudentLayout>
      <Box sx={{ maxWidth: 1200, mx: "auto", width: "100%" }}>
        {!selectedExercise ? (
          <>
            <StudentBreadcrumb
              items={[
                { label: t("My Classes"), href: "/student/classes" },
                groupName && { label: groupName, href: `/student/class/${groupId}` },
                { label: "Darssy AI" },
              ]}
            />

            <Typography sx={{ fontWeight: 700, fontSize: 26, color: "#0f172a" }}>Darssy AI</Typography>
            <Typography sx={{ color: "#64748b", mt: 0.5, mb: 3 }}>
              {t("تصفح التمارين المدعومة بالذكاء الاصطناعي وتدرب لتعزيز فهمك ومهاراتك.")}
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "minmax(0, 1fr) minmax(170px, .8fr)",
                  md: "minmax(260px, 1.4fr) minmax(170px, 1fr) minmax(210px, 1fr)",
                },
                gap: { xs: 1.25, md: 1.5 },
                width: "100%",
                maxWidth: { md: "70%" },
                mb: 3,
              }}
            >
              <TextField
                placeholder={t("Search exercises...")}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                sx={filterControlSx}
                inputProps={{
                  dir: "ltr",
                  style: { color: filterTextColor, textAlign: "left", paddingLeft: 0 },
                }}
                InputProps={{
                  sx: { direction: "ltr" },
                  startAdornment: (
                    <InputAdornment position="start" sx={{ ml: 1, mr: 0.75 }}>
                      <SearchRoundedIcon sx={{ fontSize: 25, color: filterTextColor }} />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={filterControlSx}
                SelectProps={{ IconComponent: KeyboardArrowDownRoundedIcon }}
              >
                {STATUS_FILTERS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value} sx={{ color: filterTextColor, fontSize: 14 }}>
                    {t(opt.label)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                value={modeFilter}
                onChange={(e) => setModeFilter(e.target.value)}
                sx={filterControlSx}
                SelectProps={{ IconComponent: KeyboardArrowDownRoundedIcon }}
              >
                {MODE_FILTERS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value} sx={{ color: filterTextColor, fontSize: 14 }}>
                    {t(opt.label)}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
                gap: 2,
              }}
            >
              {filteredExercises.map((exercise) => {
                const canSolve = exercise.aiEnabled && exercise.reviewStatus === "validated";
                const createdDate = formatCreatedAt(exercise.createdAt);
                return (
                  <Card
                    key={exercise.id}
                    elevation={0}
                    sx={{
                      borderRadius: 1,
                      border: "1px solid #e3ebf7",
                      boxShadow: "0 4px 14px rgba(15, 35, 85, 0.035)",
                      p: { xs: 2, md: 2.5 },
                      bgcolor: "#fff",
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 1.25 }}>
                      <Typography
                        sx={{
                          color: "#071339",
                          fontSize: { xs: 16.5, md: 18 },
                          fontWeight: 700,
                          lineHeight: 1.25,
                          minWidth: 0,
                          overflowWrap: "anywhere",
                        }}
                      >
                        {exercise.title}
                      </Typography>
                      <AiStatusChip
                        aiEnabled={exercise.aiEnabled}
                        sx={{
                          flexShrink: 0,
                          height: 34,
                          borderRadius: "10px",
                          fontSize: 13,
                          fontWeight: 700,
                          "& .MuiChip-label": { px: 1.45 },
                        }}
                      />
                    </Box>
                    <Box sx={{ mb: 2.1 }}>
                      <AssistanceTypeChip
                        mode={exercise.mode}
                        sx={{
                          height: 32,
                          borderRadius: "9px",
                          fontSize: 13,
                          fontWeight: 600,
                          "& .MuiChip-label": { px: 1.2 },
                        }}
                      />
                    </Box>

                    <Box sx={{ mb: 1.8 }}>
                      <VerificationLine
                        reviewStatus={exercise.reviewStatus}
                        sx={{ gap: 0.85, fontSize: 13.5, fontWeight: 700 }}
                        iconSx={{ fontSize: 18 }}
                      />
                    </Box>

                    <Typography
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        color: "#4f6798",
                        fontSize: 13,
                        fontWeight: 600,
                        mb: 2.5,
                      }}
                    >
                      <CalendarMonthOutlinedIcon sx={{ fontSize: 19 }} />
                      {t("Created {{date}}", { date: createdDate })}
                    </Typography>

                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<PlayArrowIcon />}
                      disabled={!canSolve}
                      onClick={() => setSelectedExerciseId(exercise.id)}
                      sx={{
                        height: 42,
                        borderRadius: "9px",
                        borderWidth: "1.5px",
                        borderColor: "#2f80ff",
                        color: "#0068ff",
                        fontSize: 14.5,
                        fontWeight: 300,
                        textTransform: "none",
                        "&:hover": {
                          borderWidth: "1.5px",
                          borderColor: "#0068ff",
                          bgcolor: "rgba(0, 104, 255, 0.04)",
                        },
                        "& .MuiButton-startIcon": {
                          mr: 1.4,
                        },
                      }}
                    >
                      {t("حل التمرين")}
                    </Button>
                  </Card>
                );
              })}

              {filteredExercises.length === 0 && (
                <Typography sx={{ color: "#94a3b8", textAlign: "center", mt: 4, gridColumn: "1 / -1" }}>
                  {t("لا توجد تمارين متاحة بعد.")}
                </Typography>
              )}
            </Box>
          </>
        ) : (
          <Box>
            <Box dir={i18n.dir()} sx={{ textAlign: i18n.dir() === "rtl" ? "right" : "left" }}>
              <StudentBreadcrumb
                items={[
                  { label: t("My Classes"), href: "/student/classes" },
                  { label: groupName || t("Class"), href: `/student/class/${groupId}` },
                  { label: "Darssy AI", onClick: () => setSelectedExerciseId(null) },
                  { label: selectedExercise.title },
                ]}
              />

              <Typography
                component="h1"
                sx={{ fontWeight: 700, fontSize: 26, color: "#0f172a", overflowWrap: "anywhere" }}
              >
                {selectedExercise.title}
              </Typography>
              <Typography sx={{ color: "#64748b", mt: 0.5, mb: 3 }}>
                {t("Solve {{title}} with Darssy AI support. Get hints, guidance, and feedback as you work.", {
                  title: selectedExercise.title,
                })}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 2, alignItems: "stretch" }}>
              <Box sx={{ flex: { md: "1 1 0" }, minWidth: 0, width: "100%" }}>
                {getSubjectImages(selectedExercise).length ? (
                  <PhotoProvider>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1.5,
                        justifyContent: getSubjectImages(selectedExercise).length === 1 ? "flex-start" : "center",
                      }}
                    >
                      {getSubjectImages(selectedExercise).map((img, index) => (
                        <PhotoView key={`${img.url}-${index}`} src={img.url}>
                          <Box
                            component="img"
                            src={img.url}
                            alt={img.name || selectedExercise.title}
                            sx={{
                              width: getSubjectImages(selectedExercise).length === 1 ? "100%" : "auto",
                              maxWidth: getSubjectImages(selectedExercise).length === 1 ? "100%" : 320,
                              maxHeight: { xs: 500, md: 600 },
                              objectFit: "contain",
                              border: "1px solid #e5e7eb",
                              borderRadius: 3,
                              bgcolor: "#fff",
                              cursor: "pointer",
                            }}
                          />
                        </PhotoView>
                      ))}
                    </Box>
                  </PhotoProvider>
                ) : (
                  <Box sx={{ height: { xs: "60vh", md: 600 } }}>
                    <iframe
                      src={selectedExercise.subjectPdfUrl}
                      title={t("PDF Viewer")}
                      style={{ width: "100%", height: "100%", border: "1px solid #e5e7eb", borderRadius: 12 }}
                    />
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  flex: { md: "1 1 0" },
                  minWidth: 0,
                  width: "100%",
                  height: { xs: "75vh", md: 600 },
                  border: "1px solid #e5e7eb",
                  borderRadius: 1,
                  bgcolor: "#fff",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <Box sx={{ px: 1.5, py: 1.25, borderBottom: "1px solid #eef1f6", flexShrink: 0 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}>
                      Darssy AI Assistant
                    </Typography>
                    <AssistanceTypeChip
                      mode={selectedExercise.mode}
                      sx={{ height: 22, fontSize: 11, fontWeight: 600 }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      fontSize: 12,
                      color: "#16a34a",
                      mt: 0.5,
                    }}
                  >
                    <CheckCircleRoundedIcon sx={{ fontSize: 15 }} />
                    {t("مساعدة بالذكاء الاصطناعي وفق إعدادات الأستاذ")}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <StudentExerciseAI
                    groupId={groupId}
                    exerciseId={selectedExercise.id}
                    exercise={selectedExercise}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </StudentLayout>
  );
}
